import axios from 'axios';
import { isTenantBoundHostname } from '@/services/tenantDomain';
import { loadTenantEntryConfig, subdomainEntryEnabled } from '@/services/tenantEntryConfig';
import {
  UserManager,
  User,
  WebStorageStateStore,
  InMemoryWebStorage,
  ErrorResponse,
  type UserManagerSettings,
} from 'oidc-client-ts';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050';
const AUTH_CLIENT_ID = import.meta.env.VITE_AUTH_CLIENT_ID || 'Nexo_React';
// `openid` yields the id_token oidc-client-ts needs to build the user; `Nexo` is the
// API scope; `offline_access` requests the rotating refresh token (D7). `profile`/`email`
// are deliberately absent — they are gated OpenIddict scope permissions the SPA is not
// granted, and the Nexo access token already carries name/email/role for decodeToken.
const AUTH_SCOPE = import.meta.env.VITE_AUTH_SCOPE || 'openid Nexo offline_access';

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
}

export interface UserInfo {
  sub: string;
  name: string;
  email: string;
  role: string[];
}

/**
 * Where a login round should return to once tokens are in hand. Carried through the
 * authorization redirect as protected OIDC `state`, so deep links survive (D13b).
 */
interface AuthState {
  returnUrl?: string;
}

class AuthService {
  // Lazily constructed so this module can be imported in a non-DOM environment (Node
  // test suites that pull in apiClient). All browser-only wiring lives in getManager().
  private managerInstance: UserManager | null = null;

  // ─── D13b: tokens live in memory only ──────────────────────────────────────
  // The oidc-client-ts UserManager keeps the customer token family in an
  // InMemoryWebStorage (wiped on every full navigation — a reload is a fresh
  // authorization-code round). We mirror the loaded user into `currentUser` so
  // the broad synchronous getToken()/isAuthenticated() surface the whole app
  // relies on stays synchronous.
  private currentUser: User | null = null;

  // "Run as user" (impersonation) is a SEPARATE, server-linked token family that
  // is NEVER persisted (D13b): a reload drops it and the silent round restores the
  // ORIGINAL identity from the auth-host cookie. It shadows `currentUser` while set.
  private impersonationToken: string | null = null;

  // Single-flight refresh (D13b): concurrent 401s await the same promise.
  private refreshInFlight: Promise<string | null> | null = null;

  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private readonly REFRESH_INTERVAL_MS = 5 * 60 * 1000; // Refresh every 5 minutes to pick up permission changes

  /**
   * Last time the USER did something (pointer, key, wheel, touch). The keep-alive
   * refresh below is gated on it, and that gate is part of the server-side idle bound
   * rather than a nicety: the server reads "last activity" off the rotating refresh
   * token, so a timer that refreshes regardless of the person would report activity
   * forever and D7's idle window could never fire on an open tab.
   */
  private lastUserActivityAt = Date.now();

  private readonly sessionEndedListeners = new Set<() => void>();

  /**
   * True once the session has ended and cannot come back without an interactive
   * sign-in. A full authorization round is a page navigation, so a fresh module —
   * and a fresh false — is what clears it; nothing on this page may.
   */
  private sessionEnded = false;

  /**
   * The server's view of this session's bounds (D7), fetched after sign-in. Advisory:
   * it decides when the user is WARNED, never whether the session is still valid — that
   * answer only ever comes from the server, at the next refresh.
   */
  private sessionPolicy: { idleTimeoutMs: number; absoluteExpiresAt: number | null } | null = null;

  /**
   * Whether the user has interacted since the last keep-alive refresh.
   *
   * This flag, not an elapsed-time window, is what gates the refresh — and the difference
   * is the whole control. A refresh moves the server's activity anchor, so ANY refresh not
   * backed by real interaction extends the very session the idle bound is meant to end.
   * Measured against a six-minute tenant window: the user stopped at 13:17:57, the timer
   * refreshed at 13:22:57 because five minutes was still "recent", and the countdown's
   * confirming refresh at 13:23:57 was then answered "not idle" — the dialog vanished and
   * the session lived on. With this flag the timer refreshes at most once after the user
   * stops, carrying their last real interaction, and the anchor then stands still.
   */
  private activitySinceLastRefresh = true;

  private lastActivityBroadcastAt = 0;

  /** Cross-tab activity is a heartbeat; a shorter interval buys nothing on an 8 h window. */
  private readonly ACTIVITY_BROADCAST_INTERVAL_MS = 20 * 1000;

  // One automatic prompt=none attempt per page load (D7 auto-challenge policy).
  // A marker, not a token — sessionStorage is explicitly allowed for it (D13b).
  private readonly SILENT_MARKER = 'nexo.auth.silentAttempted';

  // Tenant / Legal Entity context is UI-scope convenience state (not secret), kept
  // in localStorage exactly as before so request headers and the tenant picker work.
  private readonly TENANT_ID_KEY = 'tenant_id';
  private readonly TENANT_NAME_KEY = 'tenant_name';
  private readonly LEGAL_ENTITY_ID_KEY = 'nexo_legal_entity_id';
  private readonly LEGAL_ENTITY_NAME_KEY = 'nexo_legal_entity_name';

  // ABP impersonation claim types (not OIDC-remapped, stay snake_case in the JWT)
  // ABP's tenant claim in the access token (AbpClaimTypes.TenantId).
  private readonly TENANT_ID_CLAIM = 'tenantid';
  private readonly IMPERSONATOR_USER_ID_CLAIM = 'impersonator_userid';
  private readonly IMPERSONATOR_USER_NAME_CLAIM = 'impersonator_username';

  // Granted permissions and feature values fetched from ABP application-configuration API
  private grantedPermissions: string[] = [];
  private featureValues: Record<string, string> = {};
  private permissionListeners: Set<() => void> = new Set();

  // Cross-tab logout propagation (D13b) — carries NO tokens, only a signal.
  private readonly logoutChannel: BroadcastChannel | null =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('nexo-auth') : null;

  constructor() {
    if (this.logoutChannel) {
      this.logoutChannel.onmessage = (event) => {
        if (event.data?.type === 'activity') {
          // `false`: relaying a relay would have the tabs echo each other forever.
          this.noteUserActivity(false);
          return;
        }
        if (event.data?.type === 'logout') {
          this.clearLocalSession();
          if (window.location.pathname !== '/login') {
            window.location.assign('/login');
          }
          return;
        }

        // D18 item 37: an interactive login for another tenant REPLACED the shared
        // auth-host session (D7 keeps a single Identity cookie, so that is what
        // physically happens). A tab still open on the previous tenant would otherwise
        // discover it as a surprise on its next silent round. Carries no tokens —
        // only the tenant that now owns the session — and is deliberately
        // distinguishable from an ordinary logout and from an impersonation change.
        if (event.data?.type === 'session-replaced') {
          if (event.data.tenantName && event.data.tenantName === this.getTenantName()) return;
          this.clearLocalSession();
          if (window.location.pathname !== '/login') {
            window.location.assign('/login?interaction=required');
          }
        }
      };
    }
  }

  /**
   * Lazily build the oidc-client-ts UserManager. Deferred so importing this module in a
   * non-DOM environment (Node test suites) does not touch `window`; every browser auth
   * flow goes through here.
   */
  private getManager(): UserManager {
    if (this.managerInstance) return this.managerInstance;

    const settings: UserManagerSettings = {
      authority: API_BASE_URL,
      client_id: AUTH_CLIENT_ID,
      redirect_uri: `${window.location.origin}/auth/callback`,
      post_logout_redirect_uri: `${window.location.origin}/auth/logged-out`,
      response_type: 'code',
      scope: AUTH_SCOPE,
      // D13b: token family in memory only — nothing in localStorage/sessionStorage.
      userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
      // Transient authorization-request data (state, nonce, code_verifier) must survive
      // the redirect — sessionStorage is explicitly allowed for it (it is not a token).
      stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
      // authService owns the D13b lifecycle: no iframe renew, no session monitor,
      // exactly one top-level prompt=none attempt per load driven by us.
      automaticSilentRenew: false,
      monitorSession: false,
      // The SPA client is not seeded with the revocation endpoint; end_session alone
      // terminates the server-side cookie session (D13b logout requirement).
      revokeTokensOnSignout: false,
    };
    const manager = new UserManager(settings);
    manager.events.addUserLoaded((user) => {
      this.currentUser = user;
    });
    manager.events.addUserUnloaded(() => {
      this.currentUser = null;
    });
    this.managerInstance = manager;
    return manager;
  }

  // ─── D13b boot lifecycle ────────────────────────────────────────────────────

  /**
   * Load any in-memory user carried across a client-side navigation. Returns null on
   * a fresh page load (InMemoryWebStorage does not survive a reload — by design).
   */
  async loadUser(): Promise<User | null> {
    this.currentUser = await this.getManager().getUser();
    return this.currentUser;
  }

  /**
   * One automatic top-level `prompt=none` authorization round per page load (D7).
   * If the auth-host cookie session is alive it completes without UI; otherwise the
   * callback receives `login_required` and falls back to interactive login — never a
   * loop. Returns false when the attempt was already spent this load.
   */
  async beginSilentSignin(returnUrl: string): Promise<boolean> {
    if (sessionStorage.getItem(this.SILENT_MARKER)) return false;
    sessionStorage.setItem(this.SILENT_MARKER, '1');
    const state: AuthState = { returnUrl };
    await this.getManager().signinRedirect({
      prompt: 'none',
      state,
      extraQueryParams: await this.tenantQueryParam(),
    });
    return true;
  }

  /**
   * Interactive login — a full authorization round landing on the ABP login page
   * (the login UI; the React password form is retired). User-initiated, so the
   * one-attempt-per-load silent marker is cleared.
   */
  async beginInteractiveLogin(tenantId?: string, returnUrl: string = '/'): Promise<void> {
    sessionStorage.removeItem(this.SILENT_MARKER);
    if (tenantId) {
      const previous = this.getTenantId();
      if (previous && previous !== tenantId) {
        await this.clearLocalDatabase('tenant switch at login');
      }
      this.setTenantId(tenantId);
    }
    const state: AuthState = { returnUrl };
    await this.getManager().signinRedirect({
      state,
      extraQueryParams: await this.tenantQueryParam(),
    });
  }

  /**
   * Complete an authorization-code callback (silent or interactive). On success the
   * token family is in memory and permissions are fetched. Returns the deep link to
   * resume. Rethrows `login_required`/`interaction_required` so the caller can fall
   * back to interactive login. Tolerates the double invocation of the callback route
   * (React strict-mode / back-nav) by surfacing a stable result.
   */
  async completeSignin(): Promise<string> {
    const previousTenantId = this.getTenantId();

    const user = await this.getManager().signinRedirectCallback();
    this.currentUser = user;
    sessionStorage.removeItem(this.SILENT_MARKER);

    // D18: on a tenant subdomain the SPA sends no __tenant, so the tenant it ends up
    // in is whatever the auth host bound the code to. Read it back from the token so
    // the rest of the app (API headers, tenant-scoped UI) knows where it is, instead
    // of trusting a stored value the hostname may have just overridden.
    const tenantId = this.decodeToken(user.access_token)?.[this.TENANT_ID_CLAIM] ?? null;
    if (tenantId) {
      this.setTenantId(tenantId);
    } else {
      localStorage.removeItem(this.TENANT_ID_KEY);
    }

    if (previousTenantId && tenantId && previousTenantId !== tenantId) {
      this.logoutChannel?.postMessage({
        type: 'session-replaced',
        tenantName: this.getTenantName(),
      });
    }

    await this.fetchGrantedPermissions();
    // The bounds are per tenant, so they are read after the tenant is known — which a
    // completed code round is the first moment to be sure of.
    await this.loadSessionPolicy();
    this.startAutoRefresh();
    const state = user.state as AuthState | undefined;
    return state?.returnUrl || '/';
  }

  /**
   * Reset the one-attempt-per-load silent marker so a subsequent full reload performs a
   * fresh silent `prompt=none` round (D13b recovery). Used before reload-based recovery
   * from a dead token family / expired session — the silent round re-establishes the
   * session from the auth-host cookie if it is still alive, or lands on interactive login.
   */
  clearSilentAttempt(): void {
    sessionStorage.removeItem(this.SILENT_MARKER);
  }

  /** True when a callback error means the silent round needs interactive fallback. */
  isInteractionRequired(error: unknown): boolean {
    const code = (error as ErrorResponse)?.error;
    return code === 'login_required' || code === 'interaction_required' || code === 'consent_required';
  }

  /**
   * Logout: terminate the server-side auth-host cookie session (end_session — revoking
   * tokens alone is insufficient under D13b), drop the in-memory family, and broadcast
   * to other tabs (no tokens in the broadcast). Redirects to the post-logout URI.
   */
  async logout(): Promise<void> {
    this.stopAutoRefresh();
    this.impersonationToken = null;
    this.grantedPermissions = [];
    this.permissionListeners.forEach((fn) => fn());
    this.logoutChannel?.postMessage({ type: 'logout' });

    await this.clearLocalDatabase('logout');

    try {
      await this.getManager().signoutRedirect();
    } catch (error) {
      // If end_session cannot be reached, still drop the local session and land the
      // user on the login page rather than leaving them in a half-authenticated state.
      console.error('[AuthService] Sign-out redirect failed:', error);
      this.clearLocalSession();
      window.location.assign('/login');
    }
  }

  /** Local-only teardown (no server round) — used by cross-tab logout propagation. */
  private clearLocalSession(): void {
    this.stopAutoRefresh();
    this.impersonationToken = null;
    this.currentUser = null;
    void this.getManager().removeUser();
    this.grantedPermissions = [];
    this.permissionListeners.forEach((fn) => fn());
  }

  // ─── Synchronous token / identity surface (unchanged contract) ──────────────

  getToken(): string | null {
    return this.impersonationToken ?? this.currentUser?.access_token ?? null;
  }

  getRefreshToken(): string | null {
    return this.currentUser?.refresh_token ?? null;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    // A merely-expired access token is still "authenticated" while a refresh token can
    // renew it (apiClient refreshes on the next 401). Only a token that cannot decode
    // or has no session behind it is unauthenticated.
    const claims = this.decodeToken(token);
    if (!claims || !claims.exp) return false;
    if (Date.now() < claims.exp * 1000) return true;
    return !this.impersonationToken && !!this.getRefreshToken();
  }

  getCurrentUser(): UserInfo | null {
    const token = this.getToken();
    if (!token) return null;
    const claims = this.decodeToken(token);
    if (!claims) return null;
    return {
      sub: claims.sub,
      name: claims.name ?? claims.preferred_username ?? claims.unique_name ?? '',
      email: claims.email ?? '',
      role: this.normalizeRoles(claims.role),
      ...claims,
    };
  }

  private normalizeRoles(role: unknown): string[] {
    if (Array.isArray(role)) return role;
    if (typeof role === 'string') return [role];
    return [];
  }

  /**
   * Refresh the access token via the rotating refresh token (single-flight, D13b).
   * Uses the refresh_token grant (no iframe). Returns null on failure WITHOUT ending
   * the cookie session — the caller decides how to recover (silent round / interactive).
   */
  async refreshToken(): Promise<string | null> {
    // Never refresh a session that has ended. A refresh moves the server's activity
    // anchor, so one made after the deadline — by a background poll's 401, or anything
    // else still running on the page — would resurrect the very session the timer just
    // declared over. Sign-in is the only way back.
    if (this.sessionEnded) return null;
    // Never refresh while impersonating: the impersonated family has no refresh token
    // by design (R6), and the admin refresh token must not swap the identity back mid-run.
    if (this.isImpersonating()) return null;
    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = this.doRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async doRefresh(): Promise<string | null> {
    const user = this.currentUser ?? (await this.getManager().getUser());
    if (!user?.refresh_token) return null;
    try {
      const renewed = await this.getManager().signinSilent();
      if (!renewed) return null;
      this.currentUser = renewed;
      await this.fetchGrantedPermissions();
      return renewed.access_token;
    } catch (error) {
      console.error('[AuthService] Token refresh failed:', error);
      return null;
    }
  }

  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }

  /**
   * Fetch granted permissions from ABP's application-configuration API.
   * ABP does not embed permissions as JWT claims — they are server-side.
   */
  async fetchGrantedPermissions(): Promise<string[]> {
    try {
      const token = this.getToken();
      if (!token) return [];

      const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
      const tenantId = this.getTenantId();
      if (tenantId) {
        headers['__tenant'] = tenantId;
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/abp/application-configuration`,
        { headers },
      );

      const policies = response.data?.auth?.grantedPolicies;
      this.grantedPermissions = policies ? Object.keys(policies) : [];
      this.featureValues = response.data?.features?.values ?? {};

      // Notify React subscribers so useHasPermission re-renders
      this.permissionListeners.forEach((fn) => fn());

      return this.grantedPermissions;
    } catch (error) {
      console.error('[AuthService] Failed to fetch permissions:', error);
      return [];
    }
  }

  getPermissions(): string[] {
    return this.grantedPermissions;
  }

  getFeatureValues(): Record<string, string> {
    return this.featureValues;
  }

  subscribePermissions(listener: () => void): () => void {
    this.permissionListeners.add(listener);
    return () => { this.permissionListeners.delete(listener); };
  }

  // ─── Tenant / Legal Entity context (unchanged) ──────────────────────────────

  /**
   * The `__tenant` hint on the authorization request.
   *
   * D18: on a tenant subdomain the auth host DERIVES the tenant from the request's
   * redirect_uri, and a hint that disagrees gets the flow rejected before it is
   * created. So the SPA sends nothing from a tenant-bound or host-login origin — a
   * hint can only agree there, never repair anything. Pre-D18 environments have no
   * tenant subdomains, so the stored id is still what routes the login.
   *
   * Which of the two applies is the installation's answer, so it is fetched here, before
   * every authorize request, rather than trusted to have been fetched by whoever started
   * the page. A page that began on /auth/callback never fetched it, and without it the
   * hostname shape alone decides — which reads a custom domain like
   * `nexo-vida-test.mimerium.com` as a tenant subdomain and drops the hint, so "Sign in
   * again" opened the login page on the host.
   */
  private async tenantQueryParam(): Promise<Record<string, string>> {
    await loadTenantEntryConfig();
    if (this.isTenantBoundOrigin()) return {};
    const tenantId = this.getTenantId();
    return tenantId ? { __tenant: tenantId } : {};
  }

  /** True when the hostname itself decides the tenant (or decides that there is none). */
  private isTenantBoundOrigin(): boolean {
    if (typeof window === 'undefined') return false;
    return isTenantBoundHostname(window.location.hostname, subdomainEntryEnabled());
  }

  /**
   * D18: the hostname-derived tenant ALWAYS takes precedence over a stored tenant from
   * a previous session. A mismatch clears the previous tenant's local state before the
   * new flow starts — the in-memory D13b token family is already per-tab and
   * unaffected, but tenant-scoped local data must not survive the switch.
   */
  async adoptHostnameTenant(slug: string): Promise<void> {
    const previousName = this.getTenantName();
    const previousId = this.getTenantId();

    if ((previousName && previousName !== slug) || previousId) {
      // The stored id belonged to whatever tenant was last used here; it is not this
      // hostname's answer and must not be sent as a hint or used as an API header.
      await this.clearLocalDatabase('hostname-derived tenant differs from stored tenant');
      localStorage.removeItem(this.TENANT_ID_KEY);
    }

    this.setTenantName(slug);
  }

  /** The reserved host-login subdomain: a host-level session, no tenant at all. */
  async adoptHostLogin(): Promise<void> {
    if (this.getTenantId() || this.getTenantName()) {
      await this.clearLocalDatabase('switched to the host-login subdomain');
    }
    localStorage.removeItem(this.TENANT_ID_KEY);
    localStorage.removeItem(this.TENANT_NAME_KEY);
  }

  getTenantId(): string | null {
    return localStorage.getItem(this.TENANT_ID_KEY);
  }

  setTenantId(tenantId: string): void {
    localStorage.setItem(this.TENANT_ID_KEY, tenantId);
  }

  getTenantName(): string | null {
    return localStorage.getItem(this.TENANT_NAME_KEY);
  }

  setTenantName(tenantName: string): void {
    localStorage.setItem(this.TENANT_NAME_KEY, tenantName);
  }

  getLegalEntityId(): string | null {
    return localStorage.getItem(this.LEGAL_ENTITY_ID_KEY);
  }

  setLegalEntityId(legalEntityId: string | null): void {
    if (legalEntityId) {
      localStorage.setItem(this.LEGAL_ENTITY_ID_KEY, legalEntityId);
    } else {
      localStorage.removeItem(this.LEGAL_ENTITY_ID_KEY);
    }
  }

  getLegalEntityName(): string | null {
    return localStorage.getItem(this.LEGAL_ENTITY_NAME_KEY);
  }

  setLegalEntityName(legalEntityName: string | null): void {
    if (legalEntityName) {
      localStorage.setItem(this.LEGAL_ENTITY_NAME_KEY, legalEntityName);
    } else {
      localStorage.removeItem(this.LEGAL_ENTITY_NAME_KEY);
    }
  }

  // ─── Auto-refresh (picks up permission changes) ─────────────────────────────

  /**
   * Subscribers notified when the session has ended and cannot be renewed. Returns an
   * unsubscribe. Kept as a listener rather than a redirect so the app can ASK before
   * navigating — a redirect fired from an interceptor takes the page down with whatever
   * the user had typed still in it.
   */
  onSessionEnded(listener: () => void): () => void {
    this.sessionEndedListeners.add(listener);
    return () => {
      this.sessionEndedListeners.delete(listener);
    };
  }

  /**
   * Called when the session could not be renewed (API client) or its deadline passed
   * (expiry warning). LATCHES: from here on, input no longer counts as activity, no
   * refresh will be made, and no deadline is reported — the ONLY way back in is the
   * interactive sign-in the expired dialog offers. Without the latch, the click that
   * dismissed the dialog's backdrop was itself "activity", revived the deadline, and a
   * session the timer had ended kept living on whatever traffic the open page produced.
   */
  notifySessionEnded(): void {
    if (this.sessionEnded) return;
    this.sessionEnded = true;
    this.stopAutoRefresh();
    this.sessionEndedListeners.forEach((listener) => listener());
  }

  /**
   * Load this session's bounds. Failure is non-fatal and deliberately silent: without the
   * policy the app simply behaves as it did before — the expiry surfaces as a failed
   * refresh instead of a countdown — and a warning banner about a missing warning would
   * be noise.
   */
  async loadSessionPolicy(): Promise<void> {
    try {
      const token = this.getToken();
      if (!token) return;

      // Plain axios, not apiClient: apiClient imports this module for its 401 handling,
      // and the cycle would leave one of them undefined at load.
      const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
      const tenantId = this.getTenantId();
      if (tenantId) {
        headers['__tenant'] = tenantId;
      }

      const response = await axios.get<{ idleTimeout: string; absoluteExpiresAtUtc: string | null }>(
        `${API_BASE_URL}/api/session/policy`,
        { headers },
      );
      const [h = '0', m = '0', sec = '0'] = (response.data.idleTimeout ?? '').split(':');
      const idleTimeoutMs = ((Number(h) * 60 + Number(m)) * 60 + Number(sec)) * 1000;
      this.sessionPolicy = {
        idleTimeoutMs,
        absoluteExpiresAt: response.data.absoluteExpiresAtUtc
          ? Date.parse(response.data.absoluteExpiresAtUtc)
          : null,
      };
    } catch {
      this.sessionPolicy = null;
    }
  }

  /**
   * When this session ends if nothing more happens, and whether staying at the keyboard
   * can still postpone it. The absolute bound cannot be extended by definition, so a
   * "keep me signed in" button must not be offered against it — offering one that cannot
   * work is worse than no button.
   */
  getSessionDeadline(): { expiresAt: number; canExtend: boolean } | null {
    // No deadline after the end: reporting one would have the warning re-open and
    // re-announce an expiry that has already been announced.
    if (this.sessionEnded) return null;
    if (!this.sessionPolicy || !this.getRefreshToken()) return null;

    const idleDeadline = this.lastUserActivityAt + this.sessionPolicy.idleTimeoutMs;
    const absolute = this.sessionPolicy.absoluteExpiresAt;

    if (absolute !== null && absolute <= idleDeadline) {
      return { expiresAt: absolute, canExtend: false };
    }

    // While impersonating there is no refresh to make: the impersonated family is
    // memory-only and deliberately has none (R6), so extendSession cannot succeed.
    // Offering the button anyway would fail the moment it is pressed.
    return { expiresAt: idleDeadline, canExtend: !this.isImpersonating() };
  }

  /**
   * "Keep me signed in": one real refresh, and the only place a token round may stand in
   * for interaction — the user pressed a button, so it is interaction. It rotates the
   * refresh token, whose creation date is what the server reads as last activity, so this
   * postpones the idle bound at the server rather than only on screen.
   */
  async extendSession(): Promise<boolean> {
    this.noteUserActivity();
    const token = await this.refreshToken();
    if (!token) return false;
    await this.loadSessionPolicy();
    return true;
  }

  /**
   * Called by the DOM listeners in SessionActivityTracker, and by the broadcast below
   * when another tab reports interaction.
   *
   * Nothing else may call this. An earlier version had the expiry check refresh the
   * session and then mark that as activity — the token round moved the server's anchor,
   * the anchor moved the deadline, and an idle tab renewed itself forever. Only a person
   * counts as a person.
   */
  noteUserActivity(broadcast = true): void {
    // An ended session is over: the click on (or around) the expired dialog must not
    // count as activity, locally or broadcast to other tabs.
    if (this.sessionEnded) return;

    this.lastUserActivityAt = Date.now();
    this.activitySinceLastRefresh = true;

    // One activity clock across the tabs, matching the single anchor the server keeps.
    // Without it a tab left open beside the one being worked in reaches its own deadline
    // and announces an expiry that has not happened. Throttled: this is a heartbeat, not
    // an event stream.
    if (broadcast && Date.now() - this.lastActivityBroadcastAt > this.ACTIVITY_BROADCAST_INTERVAL_MS) {
      this.lastActivityBroadcastAt = Date.now();
      this.logoutChannel?.postMessage({ type: 'activity' });
    }
  }

  startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.refreshTimer = setInterval(async () => {
      if (this.isImpersonating()) return;
      // An untouched tab stops renewing. This is what lets the session go idle at all.
      if (!this.activitySinceLastRefresh) return;
      if (this.getRefreshToken()) {
        this.activitySinceLastRefresh = false;
        try {
          await this.refreshToken();
        } catch (error) {
          console.warn('[AuthService] Auto-refresh failed:', error);
        }
      }
    }, this.REFRESH_INTERVAL_MS);
  }

  /** Test seam: the timer is private, and a leaked interval breaks the next test's clock. */
  stopAutoRefreshForTests(): void {
    this.stopAutoRefresh();
  }

  private stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  async refreshTokenIfNeeded(): Promise<void> {
    if (this.getRefreshToken()) {
      await this.refreshToken();
    }
  }

  // ─── "Run as user" (impersonation) ─────────────────────────────────────────
  // Design: docs/15-designspecs/Run-As-User-Impersonation.md. Under D13b the
  // impersonated token is a memory-only, tab-confined family: starting it is an
  // in-app transition (never a reload — a reload restores the original identity),
  // and ending it can simply reload (the silent round brings the admin back).

  /**
   * Exchange the current (admin) access token for a short-lived impersonation token
   * via the `impersonation` extension grant. The token is held in memory only.
   * Callers must reset the React Query cache in-app (NOT reload) after this resolves.
   */
  async impersonate(userId: string): Promise<void> {
    const currentToken = this.getToken();
    if (!currentToken) {
      throw new Error('Not authenticated');
    }
    if (this.isImpersonating()) {
      throw new Error('Already logged in as another user');
    }

    const headers: Record<string, string> = { 'Content-Type': 'application/x-www-form-urlencoded' };
    const tenantId = this.getTenantId();
    if (tenantId) {
      headers['__tenant'] = tenantId;
    }

    let response;
    try {
      response = await axios.post<LoginResponse>(
        `${API_BASE_URL}/connect/token`,
        new URLSearchParams({
          grant_type: 'impersonation',
          user_id: userId,
          access_token: currentToken,
          client_id: AUTH_CLIENT_ID,
          // Deliberately WITHOUT offline_access — impersonated sessions get no refresh token
          scope: 'Nexo',
        }),
        { headers },
      );
    } catch (error: any) {
      throw new Error(error.response?.data?.error_description || 'Impersonation failed');
    }

    // Memory-only overlay; the admin family stays intact in the UserManager so ending
    // impersonation restores it without a server round.
    this.impersonationToken = response.data.access_token;
    // No refresh token exists for this family — the timer would be a no-op anyway.
    this.stopAutoRefresh();
    await this.fetchGrantedPermissions();
  }

  /**
   * Drop the impersonated session and restore the admin family (still in memory).
   * Callers reset the React Query cache in-app; a full reload also works and is the
   * D13b-canonical restore (the silent round re-establishes the original identity).
   *
   * Resolves to the admin's permissions as re-read with the restored token, and to none
   * when they could not be read — a failed read leaves the impersonated user's list in
   * `getPermissions()`, so that is not where a caller may look.
   */
  async endImpersonation(): Promise<string[]> {
    this.impersonationToken = null;
    if (!this.currentUser) return [];
    this.startAutoRefresh();
    if (!this.isAuthenticated() && this.getRefreshToken()) {
      await this.refreshToken();
    }
    return this.fetchGrantedPermissions();
  }

  isImpersonating(): boolean {
    if (!this.impersonationToken) return false;
    const claims = this.decodeToken(this.impersonationToken);
    return !!claims?.[this.IMPERSONATOR_USER_ID_CLAIM];
  }

  getImpersonatorName(): string | null {
    if (!this.impersonationToken) return null;
    return this.decodeToken(this.impersonationToken)?.[this.IMPERSONATOR_USER_NAME_CLAIM] ?? null;
  }

  // ─── Local database teardown ────────────────────────────────────────────────

  private async clearLocalDatabase(reason: string): Promise<void> {
    // Offline teardown FIRST, while this session can still authenticate: flush queued
    // mutations (they must post as the user who made them), then clear the outbox and
    // sync state. Anything left behind here would be replayed by the outbox watchdog on
    // the next authenticated mount — under whichever user signs in on this machine next,
    // with their tenant and legal-entity headers.
    try {
      const { handleLogout } = await import('@/offline/sync');
      const { pendingLost } = await handleLogout();
      if (pendingLost > 0) {
        console.warn(`[AuthService] Discarded ${pendingLost} unsynced offline mutation(s) on ${reason}.`);
      }
    } catch (error) {
      console.warn('[AuthService] Could not clear offline sync state:', error);
    }
    try {
      const { db } = await import('@/db/schema.generated');
      await db.customers.clear();
      console.log(`[AuthService] Cleared local database (${reason})`);
    } catch (error) {
      console.warn('[AuthService] Could not clear local database:', error);
    }
  }
}

export const authService = new AuthService();
